import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layout } from '../components/layout';
import { ArrowRight, CheckCircle2, Package, Recycle, Flame, FileText } from 'lucide-react';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.theecoenviro.com';

const products = [
  {
    id: 'plastic',
    title: 'Plastic Scrap',
    subtitle: 'PET, LDPE, HDPE',
    description: 'We supply plastic scrap to recyclers with full end-to-end traceability and competitive pricing. Our plastic scrap includes PET bottles, LDPE films, and HDPE containers.',
    image: '/images/bottle_waste.png',
    features: [
      'PET bottles and containers',
      'LDPE films and bags',
      'HDPE containers and drums',
      'Full traceability documentation',
      'Competitive pricing',
    ],
    color: 'from-blue-500 to-blue-600',
    icon: Recycle,
  },
  {
    id: 'metal',
    title: 'Metal Scrap',
    subtitle: 'Ferrous & Non-Ferrous',
    description: 'We trade MS scrap, aluminium, copper, brass, and stainless steel through authorized recycling channels with complete documentation.',
    image: '/images/blocks.png',
    features: [
      'MS scrap and iron',
      'Aluminium scrap',
      'Copper and brass',
      'Stainless steel',
      'Authorized channel trading',
    ],
    color: 'from-slate-500 to-slate-600',
    icon: Package,
  },
  {
    id: 'rdf',
    title: 'RDF & SCF',
    subtitle: 'Refuse Derived Fuel',
    description: 'We process difficult-to-recycle plastics into RDF for industrial co-processing. End-use includes cement kilns and industrial boilers.',
    image: '/images/waste.png',
    features: [
      'High calorific value',
      'Cement kiln ready',
      'Industrial boiler fuel',
      'Landfill diversion',
      'Compliance certificates',
    ],
    color: 'from-orange-500 to-orange-600',
    icon: Flame,
  },
  {
    id: 'cartons',
    title: 'Used Corrugated Cartons',
    subtitle: 'OCC & Mixed Paper',
    description: 'We recover OCC, mixed paper, and cartons from corporate and industrial sources. Channelized to paper mills and authorized recyclers.',
    image: '/images/scrap_yard.png',
    features: [
      'OCC (Old Corrugated Containers)',
      'Mixed paper grades',
      'Sorted and baled',
      'Paper mill ready',
      'Bulk quantities available',
    ],
    color: 'from-amber-500 to-amber-600',
    icon: FileText,
  },
];

export function Products() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // SSR-safe animation props
  const heroAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );
  const productAnim = useSafeAnimation(
    { opacity: 0, y: 40 },
    { opacity: 1, y: 0 },
    isInView
  );
  const ctaAnim = useSafeAnimation(
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0 }
  );

  return (
    <Layout>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Our Products | Eco Enviro - Plastic Scrap, Metal Scrap, RDF & Recyclables</title>
        <meta name="title" content="Our Products | Eco Enviro - Plastic Scrap, Metal Scrap, RDF & Recyclables" />
        <meta 
          name="description" 
          content="Eco Enviro supplies quality recyclables - plastic scrap (PET, LDPE, HDPE), metal scrap (ferrous & non-ferrous), RDF, and paper recyclables to authorized recyclers with full traceability." 
        />
        <meta name="keywords" content="plastic scrap supplier India, PET scrap, LDPE scrap, HDPE scrap, metal scrap trading, RDF supplier, paper recyclables, OCC supplier" />
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href={`${SITE_URL}/products`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/products`} />
        <meta property="og:title" content="Our Products | Eco Enviro - Recyclables & Raw Materials" />
        <meta property="og:description" content="Quality recyclables - plastic scrap, metal scrap, RDF, and paper recyclables with full traceability." />
        <meta property="og:site_name" content="Eco Enviro Circular Solutions" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@theecoenviro" />
        <meta name="twitter:title" content="Our Products | Eco Enviro - Recyclables & Raw Materials" />
        <meta name="twitter:description" content="Quality recyclables - plastic scrap, metal scrap, RDF, and paper recyclables with full traceability." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-eco-green-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={heroAnim.initial}
            animate={heroAnim.animate}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-eco-green-100 text-eco-green-700 text-sm font-medium rounded-full mb-6">
              Raw Materials
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-eco-green-900 mb-6">
              Quality Recyclables for Your Business
            </h1>
            <p className="text-xl text-gray-600">
              We supply plastic scrap, metal scrap, RDF, and paper recyclables to authorized recyclers 
              with full end-to-end traceability and competitive pricing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section ref={ref} className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                id={product.id}
                initial={productAnim.initial}
                animate={productAnim.animate}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className={`relative rounded-3xl overflow-hidden shadow-2xl ${
                    index % 2 === 1 ? 'lg:order-2' : ''
                  }`}
                >
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-80 object-cover"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${product.color} opacity-20`} />
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/60 to-transparent">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${product.color} rounded-full text-white text-sm font-medium`}>
                      <product.icon className="w-4 h-4" />
                      {product.subtitle}
                    </div>
                  </div>
                </motion.div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h2 className="text-3xl sm:text-4xl font-bold text-eco-green-900 mb-4">
                    {product.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {product.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {product.features.map((feature) => (
                      <motion.li
                        key={feature}
                        whileHover={{ x: 4 }}
                        className="flex items-center gap-3"
                      >
                        <CheckCircle2 className="w-5 h-5 text-eco-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/contact?quote=true"
                      className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${product.color} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-shadow`}
                    >
                      Request Quote
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-eco-green-800 to-eco-green-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={ctaAnim.initial}
            whileInView={ctaAnim.animate}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Looking for Bulk Quantities?
            </h2>
            <p className="text-eco-green-200 text-lg mb-8">
              We supply recyclables in bulk with consistent quality and competitive pricing. 
              Contact us for custom requirements and long-term partnerships.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?quote=true"
                className="px-8 py-4 bg-white text-eco-green-800 font-semibold rounded-xl hover:bg-eco-green-50 transition-colors"
              >
                Get Bulk Pricing
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors"
              >
                Talk to Sales
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
